import React, { useEffect, useState } from "react";
import { EyeOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../app/store";
import { fetchHistoryAllSurveys } from "../../app/surveySlice";
import { useDebounce } from "use-debounce";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb";
import { toast } from "react-toastify";
import { hasPermission } from "../../utils/permission";
import { useNavigate } from "react-router-dom";
import { getSubmitSurveyById } from "../../app/formslice";
const SurveyHistory = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { surveyList, total, loading } = useSelector(
    (state: RootState) => state.survey
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);
  const [page, setPage] = useState(1);
  const [pagesize] = useState(10);
  useEffect(() => {
    const loadSurveys = async () => {
      try {
        await dispatch(
          fetchHistoryAllSurveys({
            page,
            pagesize,
            search: debouncedSearch,
            is_admin: 1,
          })
        ).unwrap();
        //  toast.success(res.message);
      } catch (err: any) {
        toast.error(err || "Failed to fetch surveys");
      }
    };
    loadSurveys();
  }, [dispatch, page, pagesize, debouncedSearch]);

  return (
    <>
      <Breadcrumb pageName="Survey History" />
      <div className="rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-md dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1">
        <div className="max-w-full overflow-x-auto">
          <div className="flex justify-end mb-4">
            <input
              type="text"
              placeholder="Search by survey name"
              value={search}
              onChange={(e) => {
                const value = e.target.value.replace(/^\s+/, "");
                setSearch(value);
                setPage(1);
              }}
              className="w-full sm:w-1/3 rounded-md border px-4 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:outline-none"
            />
          </div>
          <table className="w-full table-auto sm:table-fixed">
            <thead>
              <tr className="bg-gray-2 text-left dark:bg-meta-4">
                <th className="py-4 px-4 text-left">Sr No</th>
                <th className="py-4 px-4 text-left">Project Name</th>
                <th className="py-4 px-4 text-left">Survey Name</th>
                <th className="py-4 px-4 text-left">User Name</th>
                <th className="py-4 px-4 text-left">Submitted At</th>
                <th className="py-4 px-4 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-6">
                    <Spin />
                  </td>
                </tr>
              ) : surveyList.length > 0 ? (
                surveyList.map((survey, index) => (
                  <tr key={survey.id}>
                    <td className="border-b py-4 px-4">
                      {(page - 1) * pagesize + index + 1}
                    </td>
                    <td className="border-b py-4 px-4">
                      {survey.project_name || "--"}
                    </td>
                    <td className="border-b py-4 px-4">
                      {survey.survey_name || "--"}
                    </td>
                    <td className="border-b py-4 px-4">
                      {survey.user_name || "--"}
                    </td>
                    <td className="border-b py-4 px-4">
                      {survey.created_at
                        ? new Date(survey.created_at).toLocaleDateString(
                            "en-GB"
                          ) // ✅ 'en-GB' gives dd/mm/yyyy
                        : "--"}
                    </td>
                    <td className="border-b py-4 px-4">
                      <div className="flex gap-2">
                        {hasPermission("survey-view") && (
                          <button
                            onClick={async () => {
                              try {
                                const resultAction = await dispatch(
                                  getSubmitSurveyById(survey.id)
                                );

                                // Check for success
                                if (
                                  getSubmitSurveyById.fulfilled.match(
                                    resultAction
                                  )
                                ) {
                                  const fetchedSurvey = resultAction.payload;

                                  navigate("/dashboard/surveyDetails", {
                                    state: { survey: fetchedSurvey },
                                  });
                                } else {
                                  console.error(
                                    "Failed to fetch survey:",
                                    resultAction.payload
                                  );
                                }
                              } catch (err) {
                                console.error(
                                  "Error dispatching getSubmitSurveyById",
                                  err
                                );
                              }
                            }}
                          >
                            <EyeOutlined />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center font-medium py-4">
                    No result found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {surveyList && surveyList.length > 0 && (
            <div className="flex justify-between items-center mt-4 mb-2 flex-wrap gap-2">
              <div className="text-sm text-gray-600">
                Showing {(page - 1) * pagesize + 1} to{" "}
                {Math.min(page * pagesize, total)} of {total} surveys
              </div>

              <div className="flex items-center gap-1 flex-wrap">
                {/* Previous Button */}
                <button
                  className={`px-3 py-1.5 text-sm rounded-md border ${
                    page === 1
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "hover:bg-blue-100 text-blue-600 border-blue-300"
                  }`}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                >
                  &lt;
                </button>

                {/* Page Numbers with Ellipsis */}
                {[...Array(Math.ceil(total / pagesize)).keys()]
                  .map((n: number) => n + 1)
                  .filter((p: number) => {
                    return (
                      p === 1 ||
                      p === Math.ceil(total / pagesize) ||
                      (p >= page - 1 && p <= page + 1)
                    );
                  })
                  .reduce((acc: (number | string)[], val: number, i, arr) => {
                    if (i > 0 && val - (arr[i - 1] as number) > 1) {
                      acc.push("...");
                    }
                    acc.push(val);
                    return acc;
                  }, [])
                  .map((p: number | string, idx: number) =>
                    typeof p === "string" ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-3 py-1.5 text-sm text-gray-500"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`px-3 py-1.5 text-sm rounded-md border ${
                          p === page
                            ? "bg-gray-300 text-gray-800"
                            : "hover:bg-blue-100 text-blue-600 border-blue-300"
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}

                {/* Next Button */}
                <button
                  className={`px-3 py-1.5 text-sm rounded-md border ${
                    page * pagesize >= total
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "hover:bg-blue-100 text-blue-600 border-blue-300"
                  }`}
                  onClick={() => setPage((prev) => prev + 1)}
                  disabled={page * pagesize >= total}
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SurveyHistory;
